// =============================================================
// Delivery & Reservation Convenience Links
// =============================================================
// These are convenience links to help users act on their
// restaurant decisions. Affiliate commissions are a bonus, not
// the primary value — focus is on user experience.
//
// Current status:
//   DoorDash:   Convenience link only (no affiliate yet)
//   Uber Eats:  Convenience link only (no affiliate yet)
//   Grubhub:    Convenience link only (no affiliate yet)
//   OpenTable:  Affiliate-ready (apply at opentable.com/affiliate)
//
// When you get affiliate approvals, flip the flags below and
// the links will automatically become tracking links.
// =============================================================

// --- Affiliate config (fill in as you get approvals) ---
const IMPACT_DOORDASH_CAMPAIGN = '';   // Impact.com DoorDash
const CJ_GRUBHUB_PID = '';             // CJ Affiliate Grubhub
const OPENTABLE_AFFILIATE_ID = '';     // OpenTable partner ID

function buildImpactLink(destinationUrl, campaignId) {
  if (!campaignId) return destinationUrl;
  const encoded = encodeURIComponent(destinationUrl);
  return `https://doordash.sjv.io/c/${campaignId}/2529376?u=${encoded}`;
}

function buildCJLink(destinationUrl, pid) {
  if (!pid) return destinationUrl;
  const encoded = encodeURIComponent(destinationUrl);
  return `https://www.anrdoezrs.net/links/100148658/type/dlg/${pid}/${encoded}`;
}

function buildOpenTableLink(restaurantName, address) {
  const q = encodeURIComponent(`${restaurantName} ${address || ''}`.trim());
  const dest = `https://www.opentable.com/s/?term=${q}&utm_source=hangrily&utm_medium=referral`;
  if (!OPENTABLE_AFFILIATE_ID) return dest;
  // OpenTable uses Awin — update format when you get your real tracking URL
  return `https://www.awin1.com/cread.php?awinmid=OPENTABLE_MID&awinaffid=${OPENTABLE_AFFILIATE_ID}&ued=${encodeURIComponent(dest)}`;
}

const AFFILIATE_CONFIG = {
  doordash: {
    name: 'DoorDash',
    color: '#FF3008',
    platform: 'doordash',
    affiliateNetwork: 'impact',
    campaignId: IMPACT_DOORDASH_CAMPAIGN,
    buildUrl: (restaurantName, address) => {
      const q = encodeURIComponent(restaurantName);
      const destination = `https://www.doordash.com/search/store/${q}/?utm_source=hangrily&utm_medium=referral&utm_campaign=restaurant_result`;
      return buildImpactLink(destination, IMPACT_DOORDASH_CAMPAIGN);
    },
  },
  ubereats: {
    name: 'Uber Eats',
    color: '#06C167',
    platform: 'ubereats',
    affiliateNetwork: 'direct',
    buildUrl: (restaurantName, address) => {
      const q = encodeURIComponent(restaurantName);
      return `https://www.ubereats.com/search?q=${q}&utm_source=hangrily&utm_medium=referral&utm_campaign=restaurant_result`;
    },
  },
  grubhub: {
    name: 'Grubhub',
    color: '#F63440',
    platform: 'grubhub',
    affiliateNetwork: 'cj',
    cjPid: CJ_GRUBHUB_PID,
    buildUrl: (restaurantName, address) => {
      const q = encodeURIComponent(restaurantName);
      const destination = `https://www.grubhub.com/search?orderMethod=delivery&searchTerm=${q}&utm_source=hangrily&utm_medium=referral&utm_campaign=restaurant_result`;
      return buildCJLink(destination, CJ_GRUBHUB_PID);
    },
  },
  opentable: {
    name: 'Reserve on OpenTable',
    color: '#DA3743',
    platform: 'opentable',
    affiliateNetwork: 'awin',
    buildUrl: (restaurantName, address) => buildOpenTableLink(restaurantName, address),
  },
};

/**
 * Get delivery ordering links for a restaurant.
 * Returns an array of { name, url, color } objects.
 */
export function getDeliveryLinks(restaurant) {
  const name = restaurant?.name || '';
  const address = restaurant?.address || '';
  if (!name) return [];

  return Object.values(AFFILIATE_CONFIG).map((svc) => ({
    name: svc.name,
    color: svc.color,
    url: svc.buildUrl(name, address),
    hasAffiliate: !!svc.campaignId || !!svc.cjPid || (svc.platform === 'opentable' && !!OPENTABLE_AFFILIATE_ID),
  }));
}

/**
 * Track a delivery click (for analytics and affiliate commissions).
 * Call this when a user clicks an ordering link.
 */
export async function trackDeliveryClick(service, restaurantName, url) {
  try {
    // Local analytics
    const clicks = JSON.parse(localStorage.getItem('hangrily_delivery_clicks') || '[]');
    clicks.push({
      service,
      restaurant: restaurantName,
      timestamp: Date.now(),
    });
    if (clicks.length > 100) clicks.splice(0, clicks.length - 100);
    localStorage.setItem('hangrily_delivery_clicks', JSON.stringify(clicks));

    // Send to backend for affiliate click tracking (if logged in)
    const token = localStorage.getItem('hangrily_auth_token');
    if (token && url) {
      const config = AFFILIATE_CONFIG[service.toLowerCase().replace(/\s/g, '')] || {};
      fetch('/api/cashback/track-click', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          platform: config.platform || service.toLowerCase(),
          affiliate_network: config.affiliateNetwork || 'direct',
          tracking_url: url,
          restaurant_name: restaurantName,
        }),
      }).catch(() => {}); // Non-critical, don't block
    }
  } catch { /* non-critical */ }
}

export default AFFILIATE_CONFIG;
